import { environment } from '../../../environments/environment';

/** ตรงกับ BE `app.use('/download', express.static(.../public'))` */
const DOWNLOAD_PREFIX = '/download/';

function storedImagePublicPrefix(): string {
  const env = environment as { storedImagePublicSubpath?: string };
  const sub = env.storedImagePublicSubpath;
  const raw = sub !== undefined && sub !== null && String(sub) !== '' ? String(sub) : 'image';
  return String(raw).replace(/\/+$/, '').replace(/^\/+/, '');
}

/**
 * แปลงค่าจาก API ให้เป็น URL ที่ใช้ใน `img` ได้ทั้ง data URL (base64) และ path ไฟล์
 *
 * - `data:image/...;base64,...` → คืนตามเดิม
 * - `http(s)://` / `//` / `blob:` → คืนตามเดิม
 * - `assets/...` → คืนตามเดิม (รูปใน Angular)
 * - Path ใต้ `public/`: ถ้ายังไม่มี prefix โฟลเดอร์รูป (เช่น DB เก็บแค่ `deliveryDetail/2026-04-24/x.png` สัมพัทธ์จาก `pathImage`)
 *   จะต่อ `environment.storedImagePublicSubpath` (default `image`) ให้ตรงกับ BE `pathImage`
 * - Legacy: `image/...` หรือขึ้นต้นด้วย prefix ที่ตั้งไว้แล้ว → ไม่ต่อซ้ำ
 * - `{environment.ip}/download/` + path (หลัง normalize)
 *
 * @param value ค่าจากฟิลด์รูป (เดี่ยว ไม่รองรับ `|` ใน string เดียว — ให้ split ที่ caller)
 * @param apiBase ถ้าส่งจะใช้แทน `environment.ip` (ไม่มี trailing slash)
 */
export function resolveImageUrl(value: string | null | undefined, apiBase?: string): string {
  if (value == null || value === '') {
    return '';
  }
  let v = String(value).trim();
  if (!v) {
    return '';
  }

  if (
    v.indexOf('data:') === 0 ||
    v.indexOf('blob:') === 0 ||
    v.indexOf('http://') === 0 ||
    v.indexOf('https://') === 0 ||
    v.indexOf('//') === 0
  ) {
    return v;
  }

  if (v.indexOf('assets/') === 0) {
    return v;
  }

  v = v.replace(/\\/g, '/');
  if (v.indexOf('public/') === 0) {
    v = v.replace(/^public\//, '');
  }

  const base = (apiBase != null && apiBase !== '')
    ? apiBase.replace(/\/+$/, '')
    : String(environment.ip || '').replace(/\/+$/, '');

  let pathNoLead = v.replace(/^\/+/, '');
  const imgPrefix = storedImagePublicPrefix();
  const underDownload = pathNoLead.indexOf('download/') === 0;
  const alreadyHasPrefix =
    imgPrefix &&
    (pathNoLead === imgPrefix || pathNoLead.indexOf(imgPrefix + '/') === 0);
  const legacyImageFolder = pathNoLead.indexOf('image/') === 0;
  if (imgPrefix && !underDownload && !alreadyHasPrefix && !legacyImageFolder) {
    pathNoLead = (imgPrefix + '/' + pathNoLead).replace(/\/+/g, '/');
  }
  if (pathNoLead.indexOf('download/') === 0) {
    return base ? base + '/' + pathNoLead : '/' + pathNoLead;
  }

  if (!base) {
    return DOWNLOAD_PREFIX + pathNoLead;
  }

  return base + DOWNLOAD_PREFIX + pathNoLead;
}

/**
 * แปลง URL ที่ resolve แล้ว (หรือ path) กลับเป็นค่าที่เก็บใน DB / ส่งกลับ BE
 * - `data:` / `blob:` → คืนตามเดิม (รูปอัปโหลดใหม่เป็น base64)
 * - `http(s)://.../download/xxx` หรือ `/download/xxx` → `xxx`
 * - path สัมพัทธ์อยู่แล้ว → คืนตามเดิม
 *
 * ใช้ตอน Save/Complete เพื่อไม่ต้อง canvas-convert รูปเก่า (Phase 3 เก็บไฟล์แล้ว)
 * ซึ่งมัก fail CORS/`img` error แล้ว UI เงียบ
 */
export function toStoredImagePath(value: string | null | undefined): string {
  if (value == null || value === '') {
    return '';
  }
  let v = String(value).trim();
  if (!v) {
    return '';
  }
  if (v.indexOf('data:') === 0 || v.indexOf('blob:') === 0) {
    return v;
  }

  const q = v.indexOf('?');
  if (q >= 0) {
    v = v.substring(0, q);
  }
  const hash = v.indexOf('#');
  if (hash >= 0) {
    v = v.substring(0, hash);
  }

  const marker = '/download/';
  const lower = v.toLowerCase();
  const idx = lower.indexOf(marker);
  let pathPart = '';
  if (idx >= 0) {
    pathPart = v.substring(idx + marker.length).replace(/^\/+/, '');
  } else if (lower.indexOf('download/') === 0) {
    pathPart = v.substring('download/'.length).replace(/^\/+/, '');
  } else {
    pathPart = v.replace(/^\/+/, '');
  }

  // DB เก็บสัมพัทธ์จาก image root (deliveryDetail/...) — ตัด prefix เช่น image/ ออก
  const imgPrefix = storedImagePublicPrefix();
  if (
    imgPrefix &&
    (pathPart === imgPrefix || pathPart.indexOf(imgPrefix + '/') === 0)
  ) {
    pathPart = pathPart.substring(imgPrefix.length).replace(/^\/+/, '');
  }
  return pathPart;
}

/**
 * เตรียมรายการรูปส่ง BE (pharmacy / delivery / production):
 * - data URL (อัปโหลดใหม่) → ส่ง base64
 * - download URL/path เก่า → ส่ง path ไม่ canvas-convert
 */
export function prepareImageListPayload(pictures: string[] | null | undefined): string {
  if (!pictures || !pictures.length) {
    return '';
  }
  const out: string[] = [];
  for (let i = 0; i < pictures.length; i++) {
    const stored = toStoredImagePath(pictures[i]);
    if (stored) {
      out.push(stored);
    }
  }
  return out.join('|');
}
